import { Container, Graphics, Rectangle, Text, type DestroyOptions } from 'pixi.js';
import { COLORS, FONT_FAMILY, RADIUS, BORDER } from '../app/theme';
import { makeText } from './widgets';
import { getOverlayCanvas } from './domOverlay';
import { hapticSelection } from '../platform/telegram';
import { AccessibleButton } from './accessibility';

export interface OptionPickerOpts {
  width: number;
  height?: number;
  options: string[];
  selected?: number;
  onChange?: (index: number) => void;
}

/** Pack an RGB number to a CSS hex string. */
function hex(c: number): string {
  return `#${c.toString(16).padStart(6, '0')}`;
}

/** Only one dropdown is open at a time across the whole app. */
let activePicker: OptionPicker | null = null;

/**
 * A real dropdown: the collapsed gold box (value + ▾) opens a selectable LIST of every option, the
 * current one highlighted; selecting one (or tapping outside) closes it. The list is a DOM popup at
 * z-index 60 — above the TextArea/TextField DOM overlays (z-index 50) — positioned over the canvas
 * with the same letterbox math TextArea uses, so it is never hidden behind a sibling textarea (the
 * Ticket page) and long option text wraps and stays readable. The collapsed value is ellipsized to the
 * box width. Public API (constructor opts, `selectedIndex`, `setOptions`, `onChange`) is unchanged, so
 * existing Search/CreateGame/Ticket call sites need no edits. Its DOM nodes are removed in destroy().
 */
export class OptionPicker extends Container {
  private options: string[];
  private idx: number;
  private readonly valueText: Text;
  private readonly chevron: Text;
  private readonly onChangeCb?: (index: number) => void;
  private readonly w: number;
  private readonly h: number;

  // DOM popup state.
  private listEl?: HTMLDivElement;
  private outsideHandler?: (e: PointerEvent) => void;
  private raf = 0;
  private lastCloseAt = 0;
  private gone = false;
  private a11yControl?: AccessibleButton;

  constructor(opts: OptionPickerOpts) {
    super();
    this.options = opts.options;
    this.idx = opts.selected ?? 0;
    this.onChangeCb = opts.onChange;
    this.w = opts.width;
    this.h = opts.height ?? 56;

    const bg = new Graphics()
      .roundRect(0, 0, this.w, this.h, RADIUS.control)
      .fill({ color: COLORS.accent })
      .stroke({ width: BORDER.base, color: COLORS.accentDark });
    this.addChild(bg);

    this.chevron = makeText('▾', { size: 30, weight: '700', fill: COLORS.buttonTextDark, align: 'center' });
    this.chevron.anchor.set(0.5);
    this.chevron.position.set(26, this.h / 2);
    this.addChild(this.chevron);

    // RTL: value right-aligned at the right edge, chevron on the left.
    this.valueText = makeText('', { size: 28, weight: '700', fill: COLORS.buttonTextDark, align: 'right' });
    this.valueText.anchor.set(1, 0.5);
    this.valueText.position.set(this.w - 16, this.h / 2);
    this.addChild(this.valueText);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(0, 0, this.w, this.h);
    this.on('pointertap', () => this.toggle());

    this.refresh();
    this.a11yControl = new AccessibleButton(this, this.w, this.h, `انتخاب ${this.valueText.text}`, () => this.toggle());
  }

  private toggle(): void {
    if (this.listEl) {
      this.close();
      return;
    }
    // Tapping the box while open closes it via the outside-listener (pointerdown), then this pointertap
    // fires microseconds later on the same physical tap — ignore it so the box doesn't instantly reopen.
    if (performance.now() - this.lastCloseAt < 300) return;
    this.open();
  }

  private open(): void {
    if (this.gone || this.options.length === 0 || this.listEl) return;
    if (activePicker && activePicker !== this) activePicker.close();
    activePicker = this;

    const list = document.createElement('div');
    list.dir = 'rtl';
    list.setAttribute('role', 'listbox');
    list.style.position = 'fixed';
    list.style.zIndex = '60';
    list.style.boxSizing = 'border-box';
    list.style.background = hex(COLORS.pillBlack);
    list.style.border = `${BORDER.base}px solid ${hex(COLORS.accentDark)}`;
    list.style.borderRadius = `${RADIUS.control}px`;
    list.style.overflowY = 'auto';
    list.style.overflowX = 'hidden';
    list.style.fontFamily = FONT_FAMILY;
    list.style.boxShadow = '0 8px 26px rgba(0,0,0,0.55)';

    this.options.forEach((opt, i) => {
      const row = document.createElement('div');
      row.textContent = opt;
      row.title = opt;
      row.dir = 'rtl';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(i === this.idx));
      row.style.padding = '11px 16px';
      row.style.textAlign = 'right';
      row.style.cursor = 'pointer';
      row.style.whiteSpace = 'normal';
      row.style.wordBreak = 'break-word';
      row.style.lineHeight = '1.35';
      row.style.borderBottom = '1px solid rgba(255,255,255,0.08)';
      const selected = i === this.idx;
      row.style.color = selected ? hex(COLORS.accent) : hex(COLORS.text);
      row.style.background = selected ? 'rgba(236,187,52,0.14)' : 'transparent';
      row.addEventListener('pointerenter', () => {
        if (i !== this.idx) row.style.background = 'rgba(236,187,52,0.20)';
      });
      row.addEventListener('pointerleave', () => {
        row.style.background = i === this.idx ? 'rgba(236,187,52,0.14)' : 'transparent';
      });
      row.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.choose(i);
      });
      list.appendChild(row);
    });
    document.body.appendChild(list);
    this.listEl = list;

    // Close on any tap outside the list. Capture phase, and NO preventDefault/stopPropagation, so the
    // same tap still reaches the Pixi canvas — tapping another picker's box closes this one AND opens
    // that one in a single tap (a viewport backdrop div would have shadowed the canvas instead).
    this.outsideHandler = (e: PointerEvent) => {
      if (this.listEl && !this.listEl.contains(e.target as Node)) this.close();
    };
    window.addEventListener('pointerdown', this.outsideHandler, true);

    this.chevron.text = '▴';
    this.reposition();
    const tick = (): void => {
      if (this.gone || !this.listEl) return;
      this.reposition();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Map the box's design-space rect to viewport pixels and place the list just below it. */
  private reposition(): void {
    const canvas = getOverlayCanvas();
    const list = this.listEl;
    if (!canvas || !list || !this.parent || !this.visible) {
      this.close();
      return;
    }
    const origin = this.toGlobal({ x: 0, y: 0 });
    const right = this.toGlobal({ x: this.w, y: 0 });
    const scale = (right.x - origin.x) / this.w || 1;
    const rect = canvas.getBoundingClientRect();
    const left = rect.left + origin.x;
    const top = rect.top + origin.y + this.h * scale + 4 * scale;
    list.style.left = `${left}px`;
    list.style.top = `${top}px`;
    list.style.width = `${this.w * scale}px`;
    list.style.maxHeight = `${Math.max(140, rect.bottom - top - 12)}px`;
    list.style.fontSize = `${28 * scale}px`;
  }

  private choose(i: number): void {
    this.idx = i;
    hapticSelection();
    this.refresh();
    this.close();
    this.onChangeCb?.(i);
  }

  private close(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.outsideHandler) {
      window.removeEventListener('pointerdown', this.outsideHandler, true);
      this.outsideHandler = undefined;
    }
    this.listEl?.remove();
    this.listEl = undefined;
    if (activePicker === this) activePicker = null;
    this.lastCloseAt = performance.now();
    this.chevron.text = '▾';
  }

  /** Set the collapsed value text, ellipsized to the box width (full text lives in the open list). */
  private refresh(): void {
    const full = this.options[this.idx] ?? '';
    this.valueText.text = full;
    this.a11yControl?.setLabel(`انتخاب ${full}`);
    const maxW = this.w - 16 - 46; // right padding + left chevron zone
    if (this.valueText.width <= maxW) return;
    let s = full;
    while (s.length > 1 && this.valueText.width > maxW) {
      s = s.slice(0, -1);
      this.valueText.text = `${s.trimEnd()}…`;
    }
  }

  get selectedIndex(): number {
    return this.idx;
  }

  setOptions(options: string[], selected = 0): void {
    this.close();
    this.options = options;
    this.idx = Math.min(selected, Math.max(0, options.length - 1));
    this.refresh();
  }

  override destroy(options?: DestroyOptions): void {
    this.gone = true;
    this.close();
    this.a11yControl?.destroy(options);
    super.destroy(options);
  }
}
